import { ReactNode, useCallback, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'

/** Copy-to-clipboard button — swaps the Copy icon to a check for a beat after a successful write. `value` is
 *  a string OR a lazy producer (so a heavy summary is only built on click). Icon-only by default; pass
 *  `className` for a bordered/labelled variant and a `children(copied)` render-prop for its text. */
export const CopyButton = ({
    value,
    label,
    className,
    children,
    onCopied,
    disabled = false,
}: {
    value: string | (() => string)
    label: string
    className?: string
    children?: (copied: boolean) => ReactNode
    /** Fired after a successful copy — e.g. for analytics on the "Copy Load" action. */
    onCopied?: () => void
    disabled?: boolean
}) => {
    const { showSnackbar } = useSnackbar()
    const [copied, setCopied] = useState(false)
    const handleCopy = useCallback(async () => {
        if (disabled) return
        try {
            if (!navigator.clipboard) throw new Error('Clipboard unavailable')
            await navigator.clipboard.writeText(typeof value === 'function' ? value() : value)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
            onCopied?.()
        } catch {
            // Clipboard blocked / insecure context — the check icon never shows, so surface the failure
            // instead of leaving a "copied" implication with no feedback.
            showSnackbar(SnackbarType.ERROR, 'Couldn’t copy to clipboard')
        }
    }, [disabled, value, showSnackbar, onCopied])
    return (
        <button
            type="button"
            onClick={handleCopy}
            aria-label={label}
            title={label}
            disabled={disabled}
            className={`${
                className ??
                'flex size-6 shrink-0 items-center justify-center rounded-md border-none bg-transparent text-foreground-subtle transition-colors hover:bg-default hover:text-foreground'
            } disabled:cursor-not-allowed disabled:opacity-50`}
        >
            {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
            {children?.(copied)}
        </button>
    )
}
